// Fetches the witness trust anchor for the hosted verifier.
//
// Witness signatures must be verified against a key the verifier trusts
// from outside the receipt, not against the key embedded in the receipt
// (which is supplied by whoever produced it). The trust root is the
// witness's published well-known keys document. This module fetches it,
// extracts the witness keys, and caches the result per witness URL with a
// short TTL. The keys change rarely, so this still avoids re-fetching per
// receipt, but it bounds how long a long-lived tab keeps trusting a stale
// anchor: if the witness rotates or DISTRUSTS a key (removes it from the
// document during a compromise), an open verifier picks that up within the
// TTL instead of trusting the removed key for the whole page lifetime. A
// fetch failure is surfaced to the caller, which must refuse to verify
// rather than silently fall back to the embedded key.

import type { WitnessKey } from "@sequesign/lib/types";
import {
  parseTrustedRegistrationKeys,
  parseTrustedAuthorKeys,
  type AuthorKey
} from "@sequesign/lib/verify";

export class TrustAnchorFetchError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TrustAnchorFetchError";
  }
}

// How long a fetched witness anchor stays cached before it is re-fetched. The
// trust anchor is security-critical (it decides which witness signatures are
// accepted), so this bounds the window in which an open tab keeps trusting a
// key the witness has since removed — the client-side half of distrusting a
// compromised key. Kept short; the document is tiny and changes rarely.
const WITNESS_ANCHOR_TTL_MS = 5 * 60_000;

// Per-URL cache of the witness anchor with the time it was fetched.
const cache = new Map<string, { keys: WitnessKey[]; fetchedAt: number }>();

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

export async function fetchTrustedWitnessKeys(witnessUrl: string): Promise<WitnessKey[]> {
  const base = witnessUrl.replace(/\/+$/, "");
  const cached = cache.get(base);
  if (cached && Date.now() - cached.fetchedAt < WITNESS_ANCHOR_TTL_MS) return cached.keys;

  const host = hostOf(base);
  const failure = new TrustAnchorFetchError(
    `Could not fetch the witness trust anchor from ${host}. Verification requires the trust anchor; please check your network connection.`
  );

  let response: Response;
  try {
    response = await fetch(`${base}/.well-known/sequesign/keys.json`, { method: "GET" });
  } catch {
    throw failure;
  }
  if (!response.ok) {
    throw failure;
  }

  let doc: unknown;
  try {
    doc = await response.json();
  } catch {
    throw failure;
  }

  const rawKeys =
    doc && typeof doc === "object" && Array.isArray((doc as { keys?: unknown }).keys)
      ? (doc as { keys: Array<Record<string, unknown>> }).keys
      : [];
  const keys: WitnessKey[] = rawKeys
    .filter((k) => k.key_type === undefined || k.key_type === "witness")
    .filter((k) => typeof k.key_id === "string" && typeof k.public_key === "string")
    .map((k) => ({ key_id: k.key_id as string, public_key: k.public_key as string }));

  if (keys.length === 0) {
    throw new TrustAnchorFetchError(
      `The witness trust anchor from ${host} contained no witness keys. Verification cannot proceed.`
    );
  }

  cache.set(base, { keys, fetchedAt: Date.now() });
  return keys;
}

// Per-session cache keyed by the normalized dashboard base URL. No TTL: unlike
// the witness anchor above, these keys only set the identity TIER (registered
// vs self_asserted) and are never a required trust anchor, so a stale entry
// cannot make an invalid receipt verify — at worst it grades an identity leg
// slightly stale until the page reloads.
const registrationCache = new Map<string, WitnessKey[]>();

// Fetches the platform registration keys (the dashboard's published
// registration-keys.json) used to verify a receipt's agent identity_proof and
// approver/counterparty identity_proofs. UNLIKE the witness anchor, these set
// the identity TIER (registered vs self_asserted / present_verified vs
// present_unverified) — they are NOT a required trust anchor for a valid
// verification. So this is best-effort: any fetch/parse failure (or a missing
// document) returns [], and those legs simply read self_asserted /
// present_unverified rather than failing the whole verification.
export async function fetchTrustedRegistrationKeys(dashboardUrl: string): Promise<WitnessKey[]> {
  const base = dashboardUrl.replace(/\/+$/, "");
  const cached = registrationCache.get(base);
  if (cached) return cached;
  try {
    const response = await fetch(`${base}/.well-known/sequesign/registration-keys.json`, {
      method: "GET"
    });
    if (!response.ok) return [];
    const keys = parseTrustedRegistrationKeys(await response.text());
    registrationCache.set(base, keys);
    return keys;
  } catch {
    return [];
  }
}

// Per-session cache keyed by the normalized dashboard base URL.
const authorCache = new Map<string, AuthorKey[]>();

// Template system Phase 5: fetches the platform template-author keys (the
// dashboard's published author-keys.json) used to grade template_authenticity.
// Like the registration anchor, this sets a grade (attested vs
// unrecognized/unattested) rather than being a required trust anchor, so it is
// best-effort: any fetch/parse failure — or an empty document (no authors
// configured, which parseTrustedAuthorKeys rejects) — returns [], and the
// receipt simply grades unrecognized/unattested rather than failing.
export async function fetchTrustedAuthorKeys(dashboardUrl: string): Promise<AuthorKey[]> {
  const base = dashboardUrl.replace(/\/+$/, "");
  const cached = authorCache.get(base);
  if (cached) return cached;
  try {
    const response = await fetch(`${base}/.well-known/sequesign/author-keys.json`, {
      method: "GET"
    });
    if (!response.ok) return [];
    const keys = parseTrustedAuthorKeys(await response.text());
    authorCache.set(base, keys);
    return keys;
  } catch {
    return [];
  }
}
